/*
 * <klaviyo-signup> — subscribes the email form inside it straight to a Klaviyo list.
 *
 * Uses Klaviyo's client-side subscriptions endpoint, which is meant for the
 * browser and takes the public site ID only — never put a private key in the
 * theme, it is public source. Without JavaScript the Shopify customer form
 * inside submits as usual, so signups are never lost.
 */
if (!customElements.get('klaviyo-signup')) {
  customElements.define(
    'klaviyo-signup',
    class KlaviyoSignup extends HTMLElement {
      static ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
      static REVISION = '2024-10-15';
      static CHECK_ICON =
        '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
        '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.2"/>' +
        '<path d="M5 8.4 7 10.4 11 5.9" stroke="currentColor" stroke-width="1.4" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';

      connectedCallback() {
        /* The listener goes on the element, not the form: this runs while the
           parser is still inside the tag, so the children don't exist yet.
           Submit events bubble, so they reach us anyway. */
        this.addEventListener('submit', this.onSubmit.bind(this));
      }

      onSubmit(event) {
        /* Without both settings this is an ordinary Shopify signup. */
        if (!this.dataset.company || !this.dataset.list) return;

        event.preventDefault();

        this.form = event.target;
        this.message = this.querySelector('[data-klaviyo-message]');

        /* A filled honeypot means a bot: swallow the submit and say nothing. */
        const honeypot = this.querySelector('[data-klaviyo-honeypot]');
        if (honeypot && honeypot.value) return;

        const email = this.form.querySelector('input[type="email"]');
        if (!email || !email.value) return;

        this.subscribe(email.value);
      }

      subscribe(email) {
        const button = this.form.querySelector('[type="submit"]');
        const { ENDPOINT, REVISION } = this.constructor;

        /* The profile takes the email only — this revision rejects a
           `subscriptions` block here, and consent follows from the call plus
           the list's own opt-in setting in Klaviyo. */
        const attributes = {
          profile: { data: { type: 'profile', attributes: { email } } },
        };
        if (this.dataset.source) attributes.custom_source = this.dataset.source;

        if (button) button.disabled = true;
        this.hideMessage();

        fetch(`${ENDPOINT}?company_id=${encodeURIComponent(this.dataset.company)}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', revision: REVISION },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes,
              relationships: { list: { data: { type: 'list', id: this.dataset.list } } },
            },
          }),
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Klaviyo responded with ${response.status}`);
            this.showMessage(this.dataset.success, true);
            this.form.reset();
          })
          .catch(() => this.showMessage(this.dataset.error, false))
          .finally(() => {
            if (button) button.disabled = false;
          });
      }

      hideMessage() {
        if (this.message) this.message.hidden = true;
      }

      showMessage(text, isSuccess) {
        if (!this.message || !text) return;

        const base = this.message.dataset.baseClass || this.message.className;
        this.message.dataset.baseClass = base;
        this.message.className = isSuccess ? `${base} ${base.split(' ')[0]}--success` : base;
        this.message.setAttribute('role', isSuccess ? 'status' : 'alert');

        this.message.innerHTML = isSuccess ? this.constructor.CHECK_ICON : '';
        this.message.appendChild(document.createTextNode(text));
        this.message.hidden = false;
      }
    }
  );
}
